// Global app theme (accent color, button shape, heading font). App-only (Kiosk has its own themes).

export const DEFAULT_THEME = { accent: "#8B5CFF", button_style: "soft", heading_font: "Outfit" };

export const PRESETS = [
  { name: "Violet", accent: "#8B5CFF" },
  { name: "Oceaan", accent: "#2563EB" },
  { name: "Smaragd", accent: "#10B981" },
  { name: "Zonsondergang", accent: "#F43F5E" },
  { name: "Amber", accent: "#F59E0B" },
  { name: "Antraciet", accent: "#475569" },
];

export const BTN_STYLES = [
  { id: "pill", label: "Pill" },
  { id: "soft", label: "Zacht" },
  { id: "sharp", label: "Hoekig" },
];

export const FONTS = [
  { id: "Outfit", label: "Outfit", css: "Outfit:wght@400;500;600;700;800;900" },
  { id: "Poppins", label: "Poppins", css: "Poppins:wght@400;500;600;700;800" },
  { id: "Space Grotesk", label: "Space Grotesk", css: "Space+Grotesk:wght@400;500;600;700" },
  { id: "Sora", label: "Sora", css: "Sora:wght@400;500;600;700;800" },
  { id: "Fraunces", label: "Fraunces", css: "Fraunces:opsz,wght@9..144,400;9..144,600;9..144,700" },
];

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  const f = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  return { r: parseInt(f.slice(0, 2), 16), g: parseInt(f.slice(2, 4), 16), b: parseInt(f.slice(4, 6), 16) };
}

function rgbToHsl({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function hslToHex(h, s, l) {
  s /= 100; l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const to = (v) => Math.round((v + m) * 255).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export function hexToHslStr(hex) {
  const { h, s, l } = rgbToHsl(hexToRgb(hex));
  return `${h} ${s}% ${l}%`;
}

function shift(hex, dh, dl) {
  const { h, s, l } = rgbToHsl(hexToRgb(hex));
  return hslToHex((h + dh + 360) % 360, clamp(s, 0, 100), clamp(l + dl, 0, 100));
}

function loadFont(font) {
  const meta = FONTS.find((f) => f.id === font) || FONTS[0];
  let link = document.getElementById("theme-font");
  if (!link) {
    link = document.createElement("link");
    link.id = "theme-font";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  link.href = `https://fonts.googleapis.com/css2?family=${meta.css}&display=swap`;
}

export function applyTheme(theme) {
  const t = { ...DEFAULT_THEME, ...(theme || {}) };
  const root = document.documentElement;
  root.style.setProperty("--accent", t.accent);
  root.style.setProperty("--accent-2", shift(t.accent, 12, 10));
  root.style.setProperty("--accent-deep", shift(t.accent, 0, -22));
  root.style.setProperty("--primary", hexToHslStr(t.accent));
  root.style.setProperty("--ring", hexToHslStr(t.accent));
  root.setAttribute("data-btn", t.button_style || "soft");
  root.style.setProperty("--font-head", `'${t.heading_font || "Outfit"}'`);
  loadFont(t.heading_font || "Outfit");
}

// Accentkleur van het huishouden. Zet ALLEEN accent-variabelen (geen knopvorm/font).
export const ACCENT_PRESETS = [
  { name: "Violet", hex: "#8B5CFF" }, { name: "Indigo", hex: "#6366F1" },
  { name: "Blauw", hex: "#3B82F6" }, { name: "Teal", hex: "#14B8A6" },
  { name: "Groen", hex: "#22C55E" }, { name: "Limoen", hex: "#65A30D" },
  { name: "Amber", hex: "#F59E0B" }, { name: "Oranje", hex: "#F97316" },
  { name: "Rood", hex: "#EF4444" }, { name: "Roze", hex: "#EC4899" },
  { name: "Fuchsia", hex: "#C026D3" }, { name: "Grafiet", hex: "#475569" },
];

export const isValidHex = (h) => typeof h === "string" && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(h);

function inkFor(hex) {
  const { r, g, b } = hexToRgb(hex);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.62 ? "#1B1440" : "#ffffff";
}

export function applyAccent(accent) {
  const c = isValidHex(accent) ? accent : DEFAULT_THEME.accent;
  const root = document.documentElement;
  root.style.setProperty("--accent", c);
  root.style.setProperty("--accent-2", shift(c, 12, 10));
  root.style.setProperty("--accent-deep", shift(c, 0, -22));
  root.style.setProperty("--accent-ink", inkFor(c));
  root.style.setProperty("--primary", hexToHslStr(c));
  root.style.setProperty("--ring", hexToHslStr(c));
}

export function applyButtonShape(style) {
  const valid = BTN_STYLES.some((b) => b.id === style) ? style : "pill";
  document.documentElement.setAttribute("data-btn", valid);
}

export function applyHeadingFont(font) {
  const valid = FONTS.some((f) => f.id === font) ? font : "Outfit";
  document.documentElement.style.setProperty("--font-head", `'${valid}'`);
  loadFont(valid);
}

// Alle app-thema-instellingen in één keer toepassen.
export function applyAppTheme({ accent, button, font } = {}) {
  applyAccent(accent);
  applyButtonShape(button);
  applyHeadingFont(font);
}
